import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// EasyStay is bilingual (English + Simplified Chinese). Translations live here
// so they can be reviewed and extended without touching page components. Every
// string key follows a `page.section.text` convention; components use the
// `useTranslation` hook + t("nav.explore") to render the correct language.
const resources = {
  en: {
    translation: {
      nav: {
        explore: "Explore Stays",
        host: "Become a Host",
        advertise: "Advertise",
        terms: "Terms",
        signIn: "Sign in",
        signUp: "Sign up",
        myBookings: "My Bookings",
        favourites: "Favourites",
        hostDashboard: "Host Dashboard",
        admin: "Admin",
        logout: "Log out",
        identityVerify: "Identity verification",
        identityVerified: "Identity (Verified)",
        verifyCta: "Verify identity",
        language: "Language",
      },
      landing: {
        kicker: "EasyStay · Zambia's short-stay marketplace",
        heroTitle1: "Stay 2 hours or 2 weeks —",
        heroTitle2: "pay how Zambia pays.",
        heroSubtitle: "Book hotels, lodges and guest houses across Zambia. Pay with MTN Mobile Money, Airtel Money, Zamtel Kwacha or your card — and receive a digital or physical key from your host.",
        cityPlaceholder: "Lusaka, Livingstone, Ndola…",
        search: "Search",
        chips: {
          verified: "Verified hosts",
          hours: "From 2 hours",
          momo: "Mobile money",
        },
        featuredCard: {
          kicker: "Featured tonight",
          title: "Riverside Lodge · Livingstone",
          meta: "From K180/hour · Digital key",
        },
        instant: {
          kicker: "Instant confirmation",
          rating: "4.9 ★",
          meta: "2,300+ nights booked",
        },
        categories: {
          all: "All",
          hotels: "Hotels",
          lodges: "Lodges",
          guestHouses: "Guest Houses",
          apartments: "Apartments",
        },
        trending: {
          kicker: "Trending this week",
          title: "Trending in Zambia",
          subtitle: "Stays other Zambians are hearting most this week.",
          heartsWeek: "{{count}} ♥ this week",
        },
        nearby: {
          kickerDetected: "Detected location",
          kickerFallback: "Zambia's most-booked city",
          nearby: "Nearby stays",
          nearYou: "Stays near you · {{city}}",
          popularIn: "Popular in {{city}}",
          subtitleDetected: "Stays within reach of where you are in {{province}}.",
          subtitleFallback: "Enable location for personalised suggestions.",
          viewAll: "View all in {{city}} →",
        },
        curated: {
          kicker: "Curated stays",
          title: "Fresh listings across Zambia",
          viewAll: "View all →",
          loading: "Loading properties…",
        },
        sponsored: "Sponsored",
        how: {
          step: "Step {{n}}",
          find: {
            title: "Find a stay",
            body: "Search by city, hours or nights. Filter by hotel, lodge or guest house.",
          },
          pay: {
            title: "Pay Zambia's way",
            body: "MTN, Airtel, Zamtel or Visa/Mastercard — one tap and you're confirmed.",
          },
          key: {
            title: "Digital or physical key",
            body: "Hosts send a digital code, or share an address to collect a physical key.",
          },
        },
        hostCta: {
          badge: "For hosts",
          title: "List your lodge, hotel or guest house.",
          body: "Zambian travellers are looking for stays as short as 2 hours. Reach them and get paid via mobile money or card.",
          button: "Start hosting",
        },
      },
      search: {
        title: "Find your Zambian stay",
        subtitle: "Browse verified hotels, lodges and guest houses across Zambia.",
        cityPlaceholder: "City (Lusaka, Livingstone…)",
        submit: "Search",
        typeAny: "Any type",
        typeHotel: "Hotel",
        typeLodge: "Lodge",
        typeGuestHouse: "Guest House",
        typeApartment: "Apartment",
        toggleList: "List",
        toggleMap: "Map",
        loading: "Loading…",
        stayCount_one: "{{count}} stay",
        stayCount_other: "{{count}} stays",
        noResults: "No stays match your search yet.",
        sponsored: "Sponsored",
      },
      property: {
        bookNow: "Book now",
        loading: "Loading…",
        hostedBy: "Hosted by {{name}}",
        newHost: "New host on EasyStay",
        verifiedHost: "Identity verified · Trusted by EasyStay",
        superHost: "Top host on EasyStay · {{rating}} ★ across {{count}} reviews",
        superhostBadge: "Superhost",
        keyLabel: "{{kind}} key",
        amenities: "Amenities",
        payments: "Payments accepted",
        reviewsHeading: "Reviews",
        reviewsSummary: "{{rating}} · {{count}} guest review",
        reviewsSummary_other: "{{rating}} · {{count}} guest reviews",
        reviewsEmpty: "Be one of the first guests to review this stay. Reviews appear here after check-out.",
        replyLabel: "reply",
        booking: {
          perHour: "/hour",
          perNight: "{{price}}/night",
          checkIn: "Check-in",
          duration: "Duration (hours) · min {{min}}h",
          keyDelivery: "Key delivery",
          collectAt: "Collect at: {{addr}}",
          paymentMethod: "Payment method",
          momoNumber: "Mobile money number",
          hours: "{{count}}h",
          days: "{{count}}d",
          totalLine: "{{price}} × {{units}}",
          nights: "{{count}} night",
          nights_other: "{{count}} nights",
          hoursUnit: "{{count}} hour",
          hoursUnit_other: "{{count}} hours",
          total: "Total",
          verifiedGuest: "Verified guest",
          creating: "Creating booking…",
          continue: "Continue to payment",
          notCharged: "You won't be charged until payment is confirmed",
          minHours: "Minimum {{n}} hours",
        },
      },
      booking: {
        title: "Booking confirmation",
        paid: "Paid & Confirmed",
        pending: "Awaiting payment",
        confirmMomo: "Confirm mobile money payment",
        payWithCard: "Pay with card",
        digitalKeyReady: "Your digital key",
        copyKey: "Copy key",
        keyCopied: "Copied to clipboard",
        checkInNow: "I've checked in",
        checkInConfirmed: "Check-in confirmed",
        checkInHelp: "Confirm your check-in so we can release the payment to your host and lock in your stay.",
        checkInThanks: "Thanks — your host has been notified.",
        sendKeyByEmail: "Email me the key",
      },
      myBookings: {
        title: "My bookings",
        toReview_one: "You have {{count}} stay to review.",
        toReview_other: "You have {{count}} stays to review.",
        noneYet: "No bookings yet.",
        findStay: "Find a stay",
        writeReview: "Write a review",
        reviewed: "You've reviewed this stay",
        keyType: "{{type}} key",
      },
      host: {
        title: "Host dashboard",
        subtitle: "Manage listings, bookings and digital key delivery.",
        newListing: "New listing",
        signInPrompt: "Sign in to access your host dashboard",
        tabs: {
          properties: "My Properties",
          bookings: "Bookings",
          reviews: "Reviews",
        },
        propsEmpty: "You haven't listed any properties yet.",
        addFirst: "Add your first listing",
        perNight: "per night",
        minLabel: "min {{h}}h",
        guests: "{{n}} guests",
        view: "View",
        noBookings: "No bookings yet.",
        guest: "Guest:",
        sendDigitalKey: "Send digital key",
        keyDelivered: "Digital key delivered:",
        noReviews: "No reviews yet. When guests review your stays, replies land here.",
        yourReply: "Your reply",
        replyPublicly: "Reply publicly",
        replyTo: "Reply to {{name}}",
        replyPlaceholder: "Thanks for staying with us…",
        postReply: "Post reply",
        cancel: "Cancel",
        sendKeyDialogTitle: "Send digital key to guest",
        keyDialogHelp: "Create a unique code (e.g. keypad PIN or lockbox code) for {{name}}. This will show inside their booking page{{smsSuffix}}.",
        keyDialogSmsSuffix: " and be SMS'd to {{phone}}",
        keyPlaceholder: "e.g. 8271-ZM",
        deliverKey: "Deliver key",
        lastSmsSent: "delivered",
        lastSmsFailed: "not sent",
        lastSms: "Last SMS: {{status}}",
      },
      payouts: {
        cancel: "Cancel",
        cancelConfirm: "Cancel this awaiting payout? The host will not be paid unless a new check-in is confirmed.",
        cancelled: "Payout cancelled",
      },
      auth: {
        login: {
          title: "Welcome back",
          subtitle: "Sign in to book stays, host, or manage your account.",
          email: "Email",
          password: "Password",
          submit: "Sign in",
          submitting: "Signing you in…",
          orGoogle: "or continue with Google",
          noAccount: "Don't have an account?",
          register: "Create one",
          forgot: "Forgot password?",
        },
        register: {
          title: "Create your account",
          subtitle: "Free to sign up. Start booking or hosting in minutes.",
          name: "Full name",
          email: "Email",
          password: "Password (min 8 characters)",
          submit: "Sign up",
          submitting: "Creating your account…",
          orGoogle: "or continue with Google",
          hasAccount: "Already have an account?",
          signIn: "Sign in",
        },
      },
      common: {
        loading: "Loading…",
        save: "Save",
        cancel: "Cancel",
        confirm: "Confirm",
        back: "Back",
        close: "Close",
        edit: "Edit",
        delete: "Delete",
      },
    },
  },
  zh: {
    translation: {
      nav: {
        explore: "探索住宿",
        host: "成为房东",
        advertise: "投放广告",
        terms: "条款",
        signIn: "登录",
        signUp: "注册",
        myBookings: "我的预订",
        favourites: "收藏",
        hostDashboard: "房东控制台",
        admin: "管理后台",
        logout: "退出登录",
        identityVerify: "身份认证",
        identityVerified: "身份(已认证)",
        verifyCta: "验证身份",
        language: "语言",
      },
      landing: {
        kicker: "EasyStay · 赞比亚短租平台",
        heroTitle1: "2 小时到 2 周 ——",
        heroTitle2: "以赞比亚人的方式付款。",
        heroSubtitle: "预订赞比亚全境的酒店、度假屋和旅馆。使用 MTN、Airtel、Zamtel Kwacha 或银行卡付款,并从房东处领取电子或实体钥匙。",
        cityPlaceholder: "卢萨卡、利文斯敦、恩多拉……",
        search: "搜索",
        chips: {
          verified: "已核实房东",
          hours: "低至 2 小时",
          momo: "手机钱包",
        },
        featuredCard: {
          kicker: "今晚精选",
          title: "河畔度假屋 · 利文斯敦",
          meta: "K180/小时起 · 电子钥匙",
        },
        instant: {
          kicker: "即时确认",
          rating: "4.9 ★",
          meta: "已成功预订 2,300+ 晚",
        },
        categories: {
          all: "全部",
          hotels: "酒店",
          lodges: "度假屋",
          guestHouses: "旅馆",
          apartments: "公寓",
        },
        trending: {
          kicker: "本周热门",
          title: "赞比亚本周热门",
          subtitle: "本周其他赞比亚人最喜爱的住宿。",
          heartsWeek: "本周 {{count}} 次收藏",
        },
        nearby: {
          kickerDetected: "已定位",
          kickerFallback: "赞比亚热门城市",
          nearby: "附近住宿",
          nearYou: "您附近的住宿 · {{city}}",
          popularIn: "{{city}} 热门",
          subtitleDetected: "在 {{province}} 触手可及的住宿。",
          subtitleFallback: "开启位置权限以获取个性化推荐。",
          viewAll: "查看 {{city}} 全部住宿 →",
        },
        curated: {
          kicker: "精选住宿",
          title: "赞比亚全境最新房源",
          viewAll: "查看全部 →",
          loading: "正在加载房源……",
        },
        sponsored: "赞助",
        how: {
          step: "第 {{n}} 步",
          find: {
            title: "寻找住宿",
            body: "按城市、小时或晚数搜索。可按酒店、度假屋或旅馆筛选。",
          },
          pay: {
            title: "以赞比亚人熟悉的方式付款",
            body: "MTN、Airtel、Zamtel 或 Visa/Mastercard——一键确认。",
          },
          key: {
            title: "电子或实体钥匙",
            body: "房东可发送电子密码,或提供地址供您领取实体钥匙。",
          },
        },
        hostCta: {
          badge: "房东专区",
          title: "上架您的度假屋、酒店或旅馆。",
          body: "赞比亚旅客正在寻找 2 小时起的短租——触达他们,通过手机钱包或银行卡收款。",
          button: "开始接待房客",
        },
      },
      search: {
        title: "找到您在赞比亚的住宿",
        subtitle: "浏览赞比亚各地已核实的酒店、度假屋和旅馆。",
        cityPlaceholder: "城市(卢萨卡、利文斯敦……)",
        submit: "搜索",
        typeAny: "全部类型",
        typeHotel: "酒店",
        typeLodge: "度假屋",
        typeGuestHouse: "旅馆",
        typeApartment: "公寓",
        toggleList: "列表",
        toggleMap: "地图",
        loading: "加载中……",
        stayCount_one: "{{count}} 处住宿",
        stayCount_other: "{{count}} 处住宿",
        noResults: "暂无符合搜索条件的住宿。",
        sponsored: "赞助",
      },
      property: {
        bookNow: "立即预订",
        loading: "加载中……",
        hostedBy: "房东:{{name}}",
        newHost: "EasyStay 新房东",
        verifiedHost: "身份已认证 · EasyStay 信任房东",
        superHost: "EasyStay 顶级房东 · 综合评分 {{rating}} ★,共 {{count}} 条评价",
        superhostBadge: "超赞房东",
        keyLabel: "{{kind}}钥匙",
        amenities: "配套设施",
        payments: "支持的付款方式",
        reviewsHeading: "评价",
        reviewsSummary: "{{rating}} · {{count}} 条房客评价",
        reviewsSummary_other: "{{rating}} · {{count}} 条房客评价",
        reviewsEmpty: "成为首位评价此住宿的房客。评价将在退房后显示于此。",
        replyLabel: "回复",
        booking: {
          perHour: "/小时",
          perNight: "{{price}}/晚",
          checkIn: "入住时间",
          duration: "时长(小时)· 最低 {{min}} 小时",
          keyDelivery: "钥匙交付方式",
          collectAt: "领取地址:{{addr}}",
          paymentMethod: "付款方式",
          momoNumber: "手机钱包号码",
          hours: "{{count}} 小时",
          days: "{{count}} 天",
          totalLine: "{{price}} × {{units}}",
          nights: "{{count}} 晚",
          nights_other: "{{count}} 晚",
          hoursUnit: "{{count}} 小时",
          hoursUnit_other: "{{count}} 小时",
          total: "合计",
          verifiedGuest: "已核实房客",
          creating: "正在创建预订……",
          continue: "继续付款",
          notCharged: "在您确认付款之前不会扣款",
          minHours: "最低 {{n}} 小时",
        },
      },
      booking: {
        title: "预订确认",
        paid: "已付款并确认",
        pending: "待支付",
        confirmMomo: "确认手机钱包付款",
        payWithCard: "使用银行卡付款",
        digitalKeyReady: "您的电子钥匙",
        copyKey: "复制密钥",
        keyCopied: "已复制到剪贴板",
        checkInNow: "我已办理入住",
        checkInConfirmed: "已确认入住",
        checkInHelp: "确认您已入住,以便我们向房东放款并锁定您的行程。",
        checkInThanks: "感谢确认——房东已收到通知。",
        sendKeyByEmail: "将密钥发送到我的邮箱",
      },
      myBookings: {
        title: "我的预订",
        toReview_one: "您还有 {{count}} 个住宿待评价。",
        toReview_other: "您还有 {{count}} 个住宿待评价。",
        noneYet: "尚无预订记录。",
        findStay: "寻找住宿",
        writeReview: "撰写评价",
        reviewed: "您已评价此次住宿",
        keyType: "{{type}}钥匙",
      },
      host: {
        title: "房东控制台",
        subtitle: "管理房源、预订与电子钥匙交付。",
        newListing: "新增房源",
        signInPrompt: "登录以访问房东控制台",
        tabs: {
          properties: "我的房源",
          bookings: "预订",
          reviews: "评价",
        },
        propsEmpty: "您尚未上架任何房源。",
        addFirst: "上架第一个房源",
        perNight: "每晚",
        minLabel: "最低 {{h}} 小时",
        guests: "{{n}} 位房客",
        view: "查看",
        noBookings: "暂无预订。",
        guest: "房客:",
        sendDigitalKey: "发送电子钥匙",
        keyDelivered: "电子钥匙已交付:",
        noReviews: "暂无评价。当房客评价您的住宿时,回复将显示于此。",
        yourReply: "您的回复",
        replyPublicly: "公开回复",
        replyTo: "回复 {{name}}",
        replyPlaceholder: "感谢入住我们的住宿……",
        postReply: "发布回复",
        cancel: "取消",
        sendKeyDialogTitle: "向房客发送电子钥匙",
        keyDialogHelp: "为 {{name}} 创建一个唯一密码(如键盘 PIN 或密码盒代码)。该密码将显示在其预订页面{{smsSuffix}}。",
        keyDialogSmsSuffix: ",并短信发送至 {{phone}}",
        keyPlaceholder: "例如 8271-ZM",
        deliverKey: "交付钥匙",
        lastSmsSent: "已送达",
        lastSmsFailed: "未发送",
        lastSms: "上次短信:{{status}}",
      },
      payouts: {
        cancel: "取消",
        cancelConfirm: "取消这笔待放款款项?除非确认新的入住,否则房东将不会收到款项。",
        cancelled: "已取消放款",
      },
      auth: {
        login: {
          title: "欢迎回来",
          subtitle: "登录以预订住宿、发布房源或管理您的账户。",
          email: "邮箱",
          password: "密码",
          submit: "登录",
          submitting: "正在登录……",
          orGoogle: "或使用 Google 账号登录",
          noAccount: "还没有账号?",
          register: "立即创建",
          forgot: "忘记密码?",
        },
        register: {
          title: "创建您的账户",
          subtitle: "免费注册,几分钟即可开始预订或成为房东。",
          name: "姓名",
          email: "邮箱",
          password: "密码(至少 8 位)",
          submit: "注册",
          submitting: "正在创建账户……",
          orGoogle: "或使用 Google 账号注册",
          hasAccount: "已有账号?",
          signIn: "登录",
        },
      },
      common: {
        loading: "加载中……",
        save: "保存",
        cancel: "取消",
        confirm: "确认",
        back: "返回",
        close: "关闭",
        edit: "编辑",
        delete: "删除",
      },
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    supportedLngs: ["en", "zh"],
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: "easystay-lang",
      caches: ["localStorage"],
    },
    interpolation: { escapeValue: false }, // React already XSS-escapes
    returnEmptyString: false,
  });

export default i18n;
